/**
 * The query parameters the guide-page snippet hands the interview.
 *
 *   ?co=Deloitte    the company the guide is about, shown in the copy
 *   ?a1=2           the answer the reader already tapped on the guide page (index into question 1)
 *   ?src=<slug>     the guide page (read by `guide-source.ts`, shared with the Labs preview)
 *   ?via=embed      running inside the guide page's frame (read by `entry-placement.ts`)
 *
 * All four are untrusted: anyone can edit the URL. `co` is rendered as TEXT (React escapes it), is
 * bounded, and is restricted to characters a company name actually uses, so a forged link cannot put
 * anything but a short name on the screen.
 */

export const COMPANY_PARAM = 'co';
export const FIRST_ANSWER_PARAM = 'a1';

const MAX_COMPANY_LENGTH = 40;
const COMPANY_RE = /^[\p{L}\p{N} &+.'’,-]+$/u;
const DIGIT_RE = /^\d$/;
const WHITESPACE_RE = /\s+/g;
const COMPANY_PLACEHOLDER_RE = /\{company\}/g;
const ARTICLE_COMPANY_RE = /\b([Aa])n? \{company\}/g;
/** "EY", "HSBC", the "NBC" of "NBCUniversal", the "S" of "S&P": read letter by letter. */
const INITIALISM_RE = /^(?:[A-Z]{2,}(?![a-z])|[A-Z](?=[&.]))/;
/** Letters whose NAME starts with a vowel sound: "an EY", "an HSBC", "an NBA". */
const VOWEL_SOUND_LETTERS = 'AEFHILMNORSX';
const VOWEL_START_RE = /^[AEIOaeio]/;

/** A safe company display name, or null when absent or not a plausible name. */
export function normaliseCompany(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  const value = raw.trim().replace(WHITESPACE_RE, ' ');
  if (!value || value.length > MAX_COMPANY_LENGTH) {
    return null;
  }
  return COMPANY_RE.test(value) ? value : null;
}

/** The first answer's index, or null when absent or outside `optionCount`. */
export function normaliseFirstAnswer(raw: string | null | undefined, optionCount: number): number | null {
  if (raw == null || !DIGIT_RE.test(raw)) {
    return null;
  }
  const index = Number(raw);
  return index < optionCount ? index : null;
}

export function readCompanyFromLocation(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return normaliseCompany(new URLSearchParams(window.location.search).get(COMPANY_PARAM));
}

export function readFirstAnswerFromLocation(optionCount: number): number | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return normaliseFirstAnswer(new URLSearchParams(window.location.search).get(FIRST_ANSWER_PARAM), optionCount);
}

/**
 * "a" or "an" for a company name. Initialisms go by the sound of their first LETTER ("an EY",
 * "a KPMG"); words by their first letter ("an Evercore", "a Nike"). "U" names stay "a" ("a Unilever"),
 * which is right more often than not for company names.
 */
export function indefiniteArticle(name: string): 'a' | 'an' {
  if (INITIALISM_RE.test(name)) {
    return VOWEL_SOUND_LETTERS.includes(name.charAt(0)) ? 'an' : 'a';
  }
  return VOWEL_START_RE.test(name) ? 'an' : 'a';
}

/**
 * Fill `{company}` in pack copy. Without a name it falls back to a neutral phrase, keeping the
 * grammar: "a {company} team" becomes "a firm's team", not "a the firm team".
 */
export function withCompany(text: string, company: string | null): string {
  if (company) {
    const article = indefiniteArticle(company);
    return text
      .replace(ARTICLE_COMPANY_RE, (_, a: string) => `${a === 'A' ? article.replace('a', 'A') : article} ${company}`)
      .replace(COMPANY_PLACEHOLDER_RE, company);
  }
  return text.replace(ARTICLE_COMPANY_RE, '$1 firm’s').replace(COMPANY_PLACEHOLDER_RE, 'the firm');
}
